import i18next from 'i18next';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import { useTranslation } from 'react-i18next';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import useUser from 'utils/useUser';
import TrashDeleteIcon from 'components/atoms/icons/trashIcon';
import { DOCUMENTATION_TIME_PLACEHOLDER } from 'infrastructure/consts/tables';
import { convertSecondsToTime } from 'utils/timeHelper';
import { tableSortMap } from 'infrastructure/antd/functions/create-sort-order';

import s from './styles.module.scss';

import type { IManualClinicNotes } from 'infrastructure/interfaces';
import type { IBaseTableColumns } from 'components/atoms/base-table/types';
import type { TDeleteCommentFn } from 'infrastructure/hooks/rpm';
import type { SortState } from 'infrastructure/types';

interface IColumnsProps {
  deleteNote: TDeleteCommentFn;
  documentationTimeSort: SortState;
}

export const columns = ({
  deleteNote,
  documentationTimeSort,
}: IColumnsProps): IBaseTableColumns<IManualClinicNotes> => {
  const { t } = useTranslation();
  const { Alert, showAlert } = useAlert();
  const { userType, isAdmin } = useUser();
  return [
    {
      label: i18next.t('labels.dateTime'),
      key: 'date',
      width: '120px',
      render(value: string) {
        return (
          <span data-cy="manual-clinic-notes-date">
            {DateFormatter({
              date: value,
              format: DateFormats['MM-dd-yyyy h:mm a'],
            })}
          </span>
        );
      },
    },
    {
      label: i18next.t('labels.documentationTime'),
      key: 'documentationTime',
      align: 'center',
      width: '120px',
      render(documentationTime?: number | null) {
        const actionTime = !documentationTime
          ? DOCUMENTATION_TIME_PLACEHOLDER
          : convertSecondsToTime(documentationTime);
        return (
          <span data-cy="manual-clinic-notes-documentation-time">
            {actionTime}
          </span>
        );
      },
      sortable: true,
      sortKey: 'documentationTime',
      sortOrder: tableSortMap[documentationTimeSort],
    },
    {
      label: i18next.t('labels.electronicallySignedBy'),
      key: 'author',
      width: '300px',
      render(value) {
        return <span data-cy="manual-clinic-notes-author">{value}</span>;
      },
    },
    {
      label: i18next.t('labels.duration'),
      key: 'timeInMinutes',
      width: '100px',
      render(value: any) {
        return <span data-cy="manual-clinic-notes-duration">{value}</span>;
      },
    },
    {
      label: i18next.t('labels.comment'),
      key: 'comment',
      width: '600px',
      responsive: ['md'],
      render(value: any, record) {
        const handleDelete = async () => {
          const message = `${t('labels.noteDeleteConfirm')} ${t(
            'labels.noteDeleteConfirmInteractiveTime',
          )}`;
          const alertAnswer = await showAlert({
            title: t('labels.commentDeleteTitle'),
            messageTitle: message,
            successBtnLabel: t('controls.yes'),
            rejectBtnLabel: t('controls.no'),
          });

          if (alertAnswer) {
            await deleteNote!({
              guid: record.guid,
              userType,
            });
          }
        };
        return (
          <div>
            {isAdmin && (
              <div className={s['top-right-actions']}>
                <button
                  className={s['delete-button']}
                  onClick={handleDelete}
                  aria-label="Delete comment"
                >
                  <TrashDeleteIcon />
                </button>
              </div>
            )}
            {Alert}
            <span data-cy="manual-clinic-notes-comment">{value}</span>
          </div>
        );
      },
    },
  ];
};
